import { createFileRoute, redirect, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/header/Header';
import { AgentHeader } from '../components/header/AgentHeader';
import { useCurrentUserPersona } from '../hooks/useCurrentUserPersona';
import { SamsungBrowserBanner } from '../components/SamsungBrowserBanner';
import { useSessionRevocationCheck } from '../hooks/useSessionRevocationCheck';

import * as Sentry from '@sentry/react';

// ECN Subtask 8 (CU-86ahjxqvp): routes an agent-only user is allowed to sit on.
// Everything else under _authenticated is a patient surface — agents are
// redirected to /sessions. Prefix match so future children are allowed.
//
// ECN Subtask 9 (CU-86ahjxrgg): /myMideoFamily is the agent-only persona's one
// other reachable surface (redesign §3.1) — their self-service view. Without it
// here, this root guard bounces agents back to /sessions when they tap the Family
// menu item. (Mirror of the mobile (app)/_layout allowlist.)
const AGENT_ALLOWED_PREFIXES = ['/sessions', '/myMideoFamily'];

function isAgentAllowedPath(pathname: string): boolean {
  return AGENT_ALLOWED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ context }) => {
    const { firebaseGetUser } = context.auth;
    const currentUser = await firebaseGetUser();
    if (!currentUser) {
      const uidMismatch = sessionStorage.getItem('uidMismatch');
      const pendingSignOut = sessionStorage.getItem('pendingSignOut');
      // Only persist requestedURL for genuine unauthenticated access — not when
      // another guard (UID mismatch or sign-out button) already handled sign-out.
      if (!uidMismatch && !pendingSignOut) {
        window.localStorage.setItem('requestedURL', window.location.href);
      } else {
        // Clear pendingSignOut now that it has served its purpose. This is the
        // only safe place to clear it — login.tsx's React effect fires too early
        // (while currentUser is still truthy from the stale render).
        if (pendingSignOut) {
          sessionStorage.removeItem('pendingSignOut');
        }
      }
      throw redirect({
        to: '/login',
      });
    }
  },
  component: WithHeader,
});

function WithHeader() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Component-level guard: beforeLoad doesn't reliably fire for child routes
  // with dynamic segments ($patientId) on all browsers. This is the fallback.
  // Skip if another guard already handled sign-out and is navigating to /login —
  // otherwise this re-fires after onAuthStateChanged propagates null and
  // overwrites sessionStorage requestedURL with a stale localStorage copy.
  if (!currentUser) {
    const uidMismatch = sessionStorage.getItem('uidMismatch');
    const pendingSignOut = sessionStorage.getItem('pendingSignOut');
    if (!uidMismatch && !pendingSignOut) {
      window.localStorage.setItem('requestedURL', window.location.href);
      navigate({ to: '/login' });
    } else {
      // Clear pendingSignOut after consuming it (fallback for when beforeLoad doesn't fire).
      if (pendingSignOut) {
        sessionStorage.removeItem('pendingSignOut');
      }
    }
    return null;
  }

  const patientId = currentUser.uid;
  Sentry.setUser({
    id: patientId,
  });

  return <WithHeaderChrome patientId={patientId} />;
}

/**
 * ECN Subtask 8 (CU-86ahjxqvp) — persona-driven chrome selection at the root.
 * Per redesign §3, the chrome variant is chosen ONCE here (not via per-item
 * conditionals scattered through the menu), so it holds on every route: an
 * agent-only user gets the minimal AgentHeader anywhere they land. Split into its
 * own component so the persona hook sits below the unauthenticated early-returns
 * in WithHeader (hooks must not run conditionally).
 */
function WithHeaderChrome({ patientId }: { patientId: string }) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // CU-86akkg3gu Part 3: ask Firebase whether this session is still valid, on
  // focus, on navigation, and on a 5-minute backstop. A revoked session's forced
  // refresh is rejected and the SDK fires onAuthStateChanged itself — this only
  // has to ASK, and it never signs anyone out. Measured: Addenda 7 and 9 of
  // docs/projects/CU-86akjurnv/CU-86akjurnv_PROBE_FORCE_REFRESH.md
  //
  // Mounted HERE because this component already sits below WithHeader's
  // unauthenticated early-returns, so `patientId` proves a signed-in session.
  useSessionRevocationCheck(pathname, true);

  const { isPatient, isMIDEOFamilyMember, personaResolved } = useCurrentUserPersona();
  const isAgentOnly = personaResolved && isMIDEOFamilyMember && !isPatient;

  // Root-level catch-all (defense-in-depth): an agent-only user who lands on ANY
  // patient route under _authenticated (stale bookmark, deep link, redirect
  // default) is sent to /sessions — their whole shell. This complements the
  // per-route guard in $patientId.tsx, which additionally runs before that
  // subtree's chart-reading gates. Wait for personaResolved so we never bounce a
  // patient on the first frame.
  //
  // Suppressed while a sign-out is in flight: during the async window after
  // firebaseSignOut() resolves but before onAuthStateChanged sets currentUser
  // null, this component is still mounted with a stale truthy user. Without the
  // pendingSignOut guard, this effect raced the sign-out's navigate('/login') and
  // looped navigate('/sessions') (TanStack throttled it → logout stalled →
  // sign-out never settled → auth persistence survived a reload). Use replace so
  // we don't stack history entries while resolving.
  const signingOut =
    typeof window !== 'undefined' && !!sessionStorage.getItem('pendingSignOut');
  const mustRedirectAgent =
    isAgentOnly && !isAgentAllowedPath(pathname) && !signingOut;
  useEffect(() => {
    if (mustRedirectAgent) {
      navigate({ to: '/sessions', replace: true });
    }
  }, [mustRedirectAgent, navigate]);

  // Don't paint a patient screen (or /sessions during sign-out teardown) while a
  // redirect is resolving — render a neutral spinner instead of <Outlet />.
  if (mustRedirectAgent) {
    return (
      <div
        role="status"
        className="flex min-h-screen flex-col items-center justify-center"
      >
        <span>Loading…</span>
      </div>
    );
  }

  return (
    <div className="bg-slate-100">
      {/* z-50: the fixed header must sit above page content. Without an explicit
          z-index it defaults to z-auto and content that creates its own stacking
          context (e.g. the MapLibre canvas on the Sessions Surface, CU-86ajd3yt0)
          paints over it on scroll. */}
      <div className="fixed top-0 z-50 w-full">
        {isAgentOnly ? <AgentHeader /> : <Header patientId={patientId} />}
      </div>
      <div className="mx-4 mt-20 mb-8 flex h-full flex-col items-center rounded-lg border-4 border-solid border-sky-700 bg-stone-100 px-4 py-8">
        <SamsungBrowserBanner />

        <Outlet />
      </div>
    </div>
  );
}
